import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MatrixVisualization from '../components/MatrixVisualization';
import MatrixExplanation from '../components/MatrixExplanation';
import MatrixDetails from '../components/MatrixDetails';
import { matrixAPI } from '../utils/api';
import { saveElementAsImage } from '../utils/imageExport';
import { shareToTelegram, shareToVK, shareToTwitter, copyToClipboard } from '../utils/share';
import { calculateMatrix } from '../utils/numerology';
import { addToLocalMatrixHistory } from '../utils/matrixHistoryLocal';
import './MatrixResultPage.scss';

const MatrixResultPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const fromState = location.state || {};
  const params = new URLSearchParams(location.search);
  const name = fromState.name || params.get('name') || '';
  const birthDate = fromState.birthDate || params.get('date') || '';
  const [matrixData, setMatrixData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    if (name && birthDate) {
      const result = calculateMatrix(name, birthDate);
      setMatrixData(result);
    }
  }, [name, birthDate]);

  if (!name || !birthDate) {
    navigate('/');
    return null;
  }

  const handleSaveImage = async () => {
    setSaving(true);
    try {
      await saveElementAsImage('matrix-result-container', `matrix-${name}-${birthDate}.png`);
    } catch (error) {
      console.error('Error saving image:', error);
      alert(t('matrix.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async (platform: 'telegram' | 'vk' | 'twitter' | 'copy') => {
    setSharing(true);
    try {
      const shareUrl = `${window.location.origin}/matrix?name=${encodeURIComponent(name)}&date=${encodeURIComponent(birthDate)}`;
      const shareOptions = {
        title: t('matrix.share.title', { name }),
        text: t('matrix.share.text'),
        url: shareUrl,
      };

      switch (platform) {
        case 'telegram':
          shareToTelegram(shareOptions);
          break;
        case 'vk':
          shareToVK(shareOptions);
          break;
        case 'twitter':
          shareToTwitter(shareOptions);
          break;
        case 'copy':
          await copyToClipboard(shareUrl);
          alert(t('matrix.share.copied'));
          break;
      }
      setShowShareMenu(false);
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setSharing(false);
    }
  };

  const handleConsultation = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/register', { state: { from: '/matrix', name, birthDate } });
      return;
    }

    // Сохраняем матрицу если пользователь авторизован
    if (matrixData) {
      try {
        await matrixAPI.save(name, birthDate, matrixData);
      } catch (error) {
        console.error('Error saving matrix:', error);
      }
    }

    navigate('/chat');
  };

  const handleSaveToHistory = async () => {
    if (!matrixData) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/register', { state: { from: '/matrix', name, birthDate } });
      return;
    }

    try {
      await matrixAPI.save(name, birthDate, matrixData);
      alert(t('matrix.saved'));
    } catch (error) {
      console.error('Error saving to history:', error);
      addToLocalMatrixHistory(name, birthDate);
      alert(t('matrix.errors.saveFailed'));
    }
  };

  return (
    <div className="matrix-result-page">
      <div className="matrix-result-page__container" id="matrix-result-container">
        <button type="button" className="page-back" onClick={() => navigate('/')} aria-label={t('nav.back')}>
          ← {t('nav.back')}
        </button>
        <header className="matrix-result-page__header">
          <h1 className="matrix-result-page__title">
            {t('matrix.title', { name })}
          </h1>
          <p className="matrix-result-page__subtitle">
            {new Date(birthDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </header>

        <div className="matrix-result-page__content">
          <div className="matrix-result-page__left">
            <MatrixVisualization name={name} birthDate={birthDate} />
            <MatrixExplanation />
          </div>
          <MatrixDetails name={name} birthDate={birthDate} />
        </div>

        <div className="matrix-result-page__actions">
          <button 
            className="btn-secondary" 
            onClick={handleSaveImage}
            disabled={saving}
          >
            {saving ? t('matrix.actions.saving') : t('matrix.actions.saveImage')}
          </button>
          <div className="share-menu-container">
            <button 
              className="btn-secondary" 
              onClick={() => setShowShareMenu(!showShareMenu)}
              disabled={sharing}
            >
              {t('matrix.actions.share')}
            </button>
            {showShareMenu && (
              <div className="share-menu">
                <button onClick={() => handleShare('telegram')}>
                  Telegram
                </button>
                <button onClick={() => handleShare('vk')}>
                  VK
                </button>
                <button onClick={() => handleShare('twitter')}>
                  Twitter
                </button>
                <button onClick={() => handleShare('copy')}>
                  {t('matrix.share.copyLink')}
                </button>
              </div>
            )}
          </div>
          <button 
            className="btn-secondary" 
            onClick={handleSaveToHistory}
          >
            {t('matrix.actions.saveToHistory')}
          </button>
        </div>

        <div className="matrix-result-page__cta">
          <button className="btn-primary btn-primary--large" onClick={handleConsultation}>
            {t('matrix.cta.consultation')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatrixResultPage;
